import * as React from "react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "@/lib/utils";

const AUTO_HEIGHT_TRANSITION = {
  duration: 0.36,
  ease: [0.22, 1, 0.36, 1] as const,
};

/**
 * Dialog content uses a ~200ms zoom/fade open. Stay on height:auto through that window
 * so we never lock a short first measure and ease up into the real size.
 */
const OPEN_SETTLE_MS = 220;

function readMaxHeightPx(): number {
  if (typeof window === "undefined") return 880;
  return Math.min(window.innerHeight * 0.92, 880);
}

/** Layout height — ignores ancestor CSS transforms (e.g. dialog zoom-in). */
function readLayoutHeight(node: HTMLElement): number {
  return Math.ceil(node.offsetHeight);
}

/**
 * Animates wrapper height to match content — used so workspace wizard dialogs
 * grow/shrink smoothly across steps and nested panels.
 *
 * Content is never forced into an inner scroll region; the dialog grows instead.
 * Only if content exceeds the viewport max does the outer shell scroll.
 */
export function WorkspaceWizardAutoHeight(props: {
  children: React.ReactNode;
  className?: string;
  /** Subtract from the scroll max (e.g. pinned footer outside this wrapper). */
  bottomReservePx?: number;
}): React.ReactElement {
  const reduceMotion = useReducedMotion();
  const measureRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = React.useState<number | "auto">("auto");
  /** Natural height on open; lock after layout settles so the first paint is not clipped. */
  const [heightLocked, setHeightLocked] = React.useState(false);
  const [animateChanges, setAnimateChanges] = React.useState(false);
  const heightLockedRef = React.useRef(false);
  const bottomReservePx = props.bottomReservePx ?? 0;
  const [maxHeightPx, setMaxHeightPx] = React.useState(() =>
    Math.max(160, readMaxHeightPx() - bottomReservePx)
  );

  React.useEffect(() => {
    const onResize = () =>
      setMaxHeightPx(Math.max(160, readMaxHeightPx() - bottomReservePx));
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [bottomReservePx]);

  React.useLayoutEffect(() => {
    const node = measureRef.current;
    if (!node) return;

    let settleTimer = 0;
    let enableAnimateRaf = 0;
    let lastMeasured = -1;

    const apply = (): number => {
      const next = readLayoutHeight(node);
      if (next < 1) return lastMeasured;
      lastMeasured = next;
      setHeight((prev) => (prev === next ? prev : next));
      return next;
    };

    const finishOpen = () => {
      if (heightLockedRef.current) return;
      apply();
      heightLockedRef.current = true;
      setHeightLocked(true);
      // Apply once more after lock (numeric height) before enabling easing.
      enableAnimateRaf = requestAnimationFrame(() => {
        apply();
        setAnimateChanges(true);
      });
    };

    const scheduleSettle = () => {
      if (heightLockedRef.current) return;
      window.clearTimeout(settleTimer);
      // Debounce: every content resize resets the open settle clock.
      settleTimer = window.setTimeout(finishOpen, OPEN_SETTLE_MS);
    };

    apply();
    scheduleSettle();

    void document.fonts?.ready?.then(() => {
      apply();
      scheduleSettle();
    });

    if (typeof ResizeObserver === "undefined") {
      return () => {
        window.clearTimeout(settleTimer);
        cancelAnimationFrame(enableAnimateRaf);
      };
    }

    const observer = new ResizeObserver(() => {
      apply();
      scheduleSettle();
    });
    observer.observe(node);
    return () => {
      window.clearTimeout(settleTimer);
      cancelAnimationFrame(enableAnimateRaf);
      observer.disconnect();
    };
  }, []);

  if (reduceMotion) {
    return (
      <div
        className={cn("overflow-y-auto", props.className)}
        style={{ maxHeight: maxHeightPx }}
      >
        <div ref={measureRef}>{props.children}</div>
      </div>
    );
  }

  const numericHeight = typeof height === "number" ? height : null;
  const isCapped = heightLocked && numericHeight != null && numericHeight > maxHeightPx + 1;
  // Stay on height:auto until settle so open is never clipped then eased taller.
  const useAutoHeight = !heightLocked || height === "auto";
  const animatedHeight = useAutoHeight ? "auto" : isCapped ? maxHeightPx : height;

  return (
    <motion.div
      className={cn("flex flex-col", props.className)}
      initial={false}
      animate={{ height: animatedHeight }}
      transition={animateChanges ? AUTO_HEIGHT_TRANSITION : { duration: 0 }}
      style={{
        overflowY: useAutoHeight ? "visible" : isCapped ? "auto" : "hidden",
        overflowX: "hidden",
      }}
    >
      {/*
        Measure natural content only. While height animates, leftover space is absorbed by
        the flex spacer (same surface as the dialog) so a pinned footer below never floats
        over bare popover gap.
      */}
      <div ref={measureRef} className="flex shrink-0 flex-col">
        {props.children}
      </div>
      {heightLocked ? <div className="min-h-0 flex-1 bg-popover" aria-hidden /> : null}
    </motion.div>
  );
}
